// Astrology Constants - Zodiac signs, planets, aspects, and element data
// Used for natal chart calculations and UI rendering

// Zodiac signs with colors, elements, and modalities
export const ZODIAC_SIGNS = [
    { name: 'Aries', symbol: '♈', element: 'Fire', modality: 'Cardinal', ruler: 'Mars', color: '#FF6B6B', gradient: 'from-red-500 to-orange-500' },
    { name: 'Taurus', symbol: '♉', element: 'Earth', modality: 'Fixed', ruler: 'Venus', color: '#4CAF50', gradient: 'from-green-600 to-emerald-500' },
    { name: 'Gemini', symbol: '♊', element: 'Air', modality: 'Mutable', ruler: 'Mercury', color: '#FFD93D', gradient: 'from-yellow-400 to-amber-300' },
    { name: 'Cancer', symbol: '♋', element: 'Water', modality: 'Cardinal', ruler: 'Moon', color: '#6FBEDF', gradient: 'from-blue-400 to-cyan-400' },
    { name: 'Leo', symbol: '♌', element: 'Fire', modality: 'Fixed', ruler: 'Sun', color: '#FF9F43', gradient: 'from-orange-500 to-yellow-500' },
    { name: 'Virgo', symbol: '♍', element: 'Earth', modality: 'Mutable', ruler: 'Mercury', color: '#8BC34A', gradient: 'from-green-500 to-lime-500' },
    { name: 'Libra', symbol: '♎', element: 'Air', modality: 'Cardinal', ruler: 'Venus', color: '#E91E8C', gradient: 'from-pink-500 to-rose-400' },
    { name: 'Scorpio', symbol: '♏', element: 'Water', modality: 'Fixed', ruler: 'Pluto', color: '#8E44AD', gradient: 'from-purple-700 to-violet-600' },
    { name: 'Sagittarius', symbol: '♐', element: 'Fire', modality: 'Mutable', ruler: 'Jupiter', color: '#9B59B6', gradient: 'from-purple-500 to-fuchsia-500' },
    { name: 'Capricorn', symbol: '♑', element: 'Earth', modality: 'Cardinal', ruler: 'Saturn', color: '#5D6D7E', gradient: 'from-gray-600 to-slate-500' },
    { name: 'Aquarius', symbol: '♒', element: 'Air', modality: 'Fixed', ruler: 'Uranus', color: '#00CED1', gradient: 'from-cyan-500 to-teal-400' },
    { name: 'Pisces', symbol: '♓', element: 'Water', modality: 'Mutable', ruler: 'Neptune', color: '#6C5CE7', gradient: 'from-indigo-500 to-purple-500' }
];

// Planet data with colors and meanings
export const PLANETS = [
    { name: 'Sun', symbol: '☉', color: '#FFD700', glowColor: 'rgba(255, 215, 0, 0.5)', meaning: 'Core identity, ego, life force', keywords: ['identity', 'vitality', 'purpose'] },
    { name: 'Moon', symbol: '☽', color: '#C0C0C0', glowColor: 'rgba(192, 192, 192, 0.5)', meaning: 'Emotions, instincts, inner self', keywords: ['emotions', 'nurturing', 'subconscious'] },
    { name: 'Mercury', symbol: '☿', color: '#C4A484', glowColor: 'rgba(196, 164, 132, 0.5)', meaning: 'Communication, thought, learning', keywords: ['communication', 'intellect', 'adaptability'] },
    { name: 'Venus', symbol: '♀', color: '#FF69B4', glowColor: 'rgba(255, 105, 180, 0.5)', meaning: 'Love, beauty, values, harmony', keywords: ['love', 'beauty', 'pleasure'] },
    { name: 'Mars', symbol: '♂', color: '#FF4500', glowColor: 'rgba(255, 69, 0, 0.5)', meaning: 'Action, desire, courage, energy', keywords: ['action', 'passion', 'drive'] },
    { name: 'Jupiter', symbol: '♃', color: '#DAA520', glowColor: 'rgba(218, 165, 32, 0.5)', meaning: 'Growth, luck, wisdom, expansion', keywords: ['expansion', 'optimism', 'wisdom'] },
    { name: 'Saturn', symbol: '♄', color: '#8B7355', glowColor: 'rgba(139, 115, 85, 0.5)', meaning: 'Structure, discipline, lessons', keywords: ['discipline', 'responsibility', 'lessons'] },
    { name: 'Uranus', symbol: '♅', color: '#40E0D0', glowColor: 'rgba(64, 224, 208, 0.5)', meaning: 'Innovation, rebellion, awakening', keywords: ['innovation', 'freedom', 'originality'] },
    { name: 'Neptune', symbol: '♆', color: '#4169E1', glowColor: 'rgba(65, 105, 225, 0.5)', meaning: 'Dreams, spirituality, illusion', keywords: ['intuition', 'dreams', 'mysticism'] },
    { name: 'Pluto', symbol: '♇', color: '#800000', glowColor: 'rgba(128, 0, 0, 0.5)', meaning: 'Transformation, power, rebirth', keywords: ['transformation', 'power', 'rebirth'] }
];

// Aspect definitions with colors and orbs
export const ASPECTS = [
    { name: 'Conjunction', angle: 0, orb: 8, symbol: '☌', color: '#FFD700', nature: 'major', effect: 'fusion' },
    { name: 'Sextile', angle: 60, orb: 6, symbol: '⚹', color: '#4CAF50', nature: 'major', effect: 'opportunity' },
    { name: 'Square', angle: 90, orb: 8, symbol: '□', color: '#FF4444', nature: 'major', effect: 'challenge' },
    { name: 'Trine', angle: 120, orb: 8, symbol: '△', color: '#00CED1', nature: 'major', effect: 'harmony' },
    { name: 'Opposition', angle: 180, orb: 8, symbol: '☍', color: '#FF6B6B', nature: 'major', effect: 'tension' }
];

// Element colors and characteristics
export const ELEMENTS = {
    Fire: { color: '#FF6B6B', gradient: 'from-red-500 to-orange-400', traits: ['passionate', 'dynamic', 'brave'] },
    Earth: { color: '#4CAF50', gradient: 'from-green-600 to-emerald-500', traits: ['practical', 'stable', 'grounded'] },
    Air: { color: '#64B5F6', gradient: 'from-blue-400 to-sky-300', traits: ['intellectual', 'social', 'curious'] },
    Water: { color: '#6C5CE7', gradient: 'from-indigo-500 to-purple-500', traits: ['emotional', 'intuitive', 'nurturing'] }
};

// House meanings
export const HOUSES = [
    { number: 1, name: 'Self', meaning: 'Identity, appearance, first impressions' },
    { number: 2, name: 'Possessions', meaning: 'Money, values, material security' },
    { number: 3, name: 'Communication', meaning: 'Siblings, learning, local travel' },
    { number: 4, name: 'Home', meaning: 'Family, roots, emotional foundation' },
    { number: 5, name: 'Pleasure', meaning: 'Creativity, romance, children' },
    { number: 6, name: 'Health', meaning: 'Work, service, daily routines' },
    { number: 7, name: 'Partnership', meaning: 'Marriage, contracts, open enemies' },
    { number: 8, name: 'Transformation', meaning: 'Death, rebirth, shared resources' },
    { number: 9, name: 'Philosophy', meaning: 'Higher learning, travel, beliefs' },
    { number: 10, name: 'Career', meaning: 'Public image, ambition, achievement' },
    { number: 11, name: 'Community', meaning: 'Friends, groups, hopes, wishes' },
    { number: 12, name: 'Spirituality', meaning: 'Hidden matters, secrets, karma' }
];

// Get zodiac sign by index (0-11)
export const getZodiacSign = (index) => ZODIAC_SIGNS[index % 12];

// Get zodiac sign from ecliptic longitude
export const getZodiacFromLongitude = (longitude) => {
    const signIndex = Math.floor(longitude / 30) % 12;
    const degree = longitude % 30;
    return {
        ...ZODIAC_SIGNS[signIndex],
        degree: Math.floor(degree),
        minute: Math.floor((degree % 1) * 60)
    };
};

// Get planet data by name
export const getPlanetByName = (name) => PLANETS.find(p => p.name === name);

// Format position string (e.g., "15°23' Taurus")
export const formatPosition = (degree, minute, sign) => {
    return `${degree}°${minute.toString().padStart(2, '0')}' ${sign}`;
};

// Check if two planets form an aspect
export const checkAspect = (lon1, lon2) => {
    let angle = Math.abs(lon1 - lon2);
    if (angle > 180) angle = 360 - angle;

    for (const aspect of ASPECTS) {
        const diff = Math.abs(angle - aspect.angle);
        if (diff <= aspect.orb) {
            return { ...aspect, actualAngle: angle, orb: diff };
        }
    }
    return null;
};
